"use client"

import { useState } from "react"
import { useSelector } from "react-redux"
import {
  Typography,
  Container,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Avatar,
  Box,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { useTheme } from "@mui/material/styles"
import BackIcon from "../common/components/BackIcon"
import NearMeIcon from "@mui/icons-material/NearMe"
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled"
import BatteryCharging90Icon from "@mui/icons-material/BatteryCharging90"
import SpeedIcon from "@mui/icons-material/Speed"
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useNavigate, useParams } from "react-router-dom"
import { useEffectAsync } from "../reactHelper"
import { useTranslation } from "../common/components/LocalizationProvider"
import PositionValue from "../common/components/PositionValue"
import usePositionAttributes from "../common/attributes/usePositionAttributes"
import { prefixString } from "../common/util/stringUtils"
import backgroundHeader from "../resources/images/custom/waves-white2.svg"
import {
  courseFormatter,
  formatDistance,
  formatSpeed,
  formatDate,
  getStatusText,
  formatVolume,
  formatAlarm,
  formatBoolean,
} from "../common/util/formatter"
import { useAttributePreference } from "../common/util/preferences"
import moment from "moment"

// Styled components
const Root = styled("div")(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
}))

const Content = styled("div")(({ theme }) => ({
  overflow: "auto",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}))

const Header = styled(Paper)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "row",
  minHeight: 300,
  margin: theme.spacing(2.5),
  borderRadius: 20,
  transition: "0.3s",
  background: "linear-gradient(310deg, rgb(98,117,148), rgb(168,184,216))",
  boxShadow: "0 8px 40px -12px rgba(0,0,0,0.3)",
  padding: theme.spacing(1.0),
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
}))

const CardPosition = styled(Paper)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "row",
  flex: 1,
  width: "100%",
  height: "100%", // importante
  borderRadius: 20,
  minHeight: 120, // pode aumentar para uniformizar ainda mais
  transition: "0.3s",
  background: "linear-gradient(310deg, rgb(98, 117, 148), rgb(168, 184, 216))",
  boxShadow: "0 8px 40px -12px rgba(0,0,0,0.3)",
  "&:hover": {
    boxShadow: "0 16px 70px -12.125px rgba(0,0,0,0.3)",
    transform: "translateY(-4px)",
  },
  padding: theme.spacing(1.5, 2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 1.5),
  },
}))

const CardContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  flex: 1,
}))

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: "#fff",
  opacity: 0.9,
  marginBottom: theme.spacing(0.25),
  lineHeight: 1.2,
}))

const CardValue = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: "bold",
  color: "#fff",
  lineHeight: 1.2,
}))

const CardIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: "#fff",
  width: 36,
  height: 36,
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
}))

const DeviceImageContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  height: "100%",
}))

const DeviceImage = styled(Box)(({ theme }) => ({
  maxHeight: 140,
  minHeight: 140,
  color: "#fff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}))

const DeviceImageName = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: "bolder",
  color: "#fff",
  textAlign: "center",
  marginTop: theme.spacing(1),
}))

const ImagePlateName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: "#fff",
  textAlign: "center",
}))

const Address = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  margin: theme.spacing(0, 0),
  color: "#fff",
  textAlign: "center",
  wordBreak: "break-word",
  maxWidth: "90%",
}))

const CourseIconContainer = styled(Avatar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  margin: "0 auto",
  height: 80,
  width: 80,
  backgroundColor: "rgba(255,255,255,0)",
  color: "#fff",
}))

const CourseTitle = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(0.5, 0, 0.5, 0),
  fontSize: 16,
  color: "#fff",
  textAlign: "center",
}))

const SectionCategoryContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-around",
  color: "#fff",
}))

const SectionCategory = styled(Box)(({ theme }) => ({
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  flex: 1,
  "&.text-right": {
    textAlign: "right",
  },
}))

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: "bold",
  color: "#fff",
  marginBottom: theme.spacing(0.5),
  lineHeight: 1.2,
}))

const CategoryValue = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: "bold",
  color: "#fff",
  lineHeight: 1.2,
}))

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: theme.spacing(2),
  borderRadius: 10,
  overflow: "hidden",
  "&::before": { display: "none" },
  [theme.breakpoints.down("sm")]: { margin: theme.spacing(1) },
}))

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  paddingBottom: theme.spacing(2),
}))

const PositionPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const t = useTranslation()
  const positionAttributes = usePositionAttributes(t)
  const speedUnit = useAttributePreference("speedUnit")
  const distanceUnit = useAttributePreference("distanceUnit")
  const volumeUnit = useAttributePreference("volumeUnit")
  const dateTimeFormat = useAttributePreference("dateTimeFormat", "YYYY-MM-DD HH:mm:ss")
  const { id } = useParams()
  const [item, setItem] = useState(null)
  const [trip, setTrip] = useState({
    distance: 0,
    averageSpeed: 0,
    spentFuel: 0,
  })
  const [loading, setLoading] = useState(true)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.down("md"))
  const [deviceImageUrl, setDeviceImageUrl] = useState(null)
  const DEFAULT_IMAGE_URL =
    "https://thumbs.dreamstime.com/b/vector-illustration-isolated-white-background-user-profile-avatar-black-line-icon-user-profile-avatar-black-solid-icon-121102166.jpg"

  useEffectAsync(async () => {
    if (id) {
      setLoading(true)
      try {
        const response = await fetch(`/api/positions?id=${id}`)
        if (response.ok) {
          const positions = await response.json()
          if (positions.length > 0) {
            setItem(positions[0])
          }

          if (positions.length > 0) {
            const deviceId = positions[0].deviceId
            const from = moment(positions[0].fixTime).startOf("day").toISOString()
            const to = moment(positions[0].fixTime).endOf("day").toISOString()
            const mail = false

            const query = new URLSearchParams({ deviceId, from, to, mail })
            try {
              const responseTrip = await fetch(`/api/reports/trips?${query.toString()}`, {
                headers: { Accept: "application/json" },
              })
              if (responseTrip.ok) {
                const contentType = responseTrip.headers.get("content-type")
                if (contentType && contentType === "application/json") {
                  const trips = await responseTrip.json()
                  if (trips.length > 0) {
                    let distance = 0
                    let averageSpeed = 0
                    let spentFuel = 0
                    trips.forEach((trip) => {
                      distance += trip.distance
                      averageSpeed += trip.averageSpeed
                      spentFuel += trip.spentFuel
                    })
                    setTrip({
                      distance,
                      averageSpeed: averageSpeed / trips.length,
                      spentFuel,
                    })
                  }
                }
              }
            } catch (error) {
              console.error("Error fetching trip data:", error)
            }
          }
        } else {
          console.error("Error fetching position data:", await response.text())
        }
      } catch (error) {
        console.error("Error:", error)
      } finally {
        setLoading(false)
      }
    }
  }, [id])

  const device = useSelector((state) => {
    if (item) {
      const d = state.devices.items[item.deviceId]
      if (d) {
        return d
      }
    }
    return null
  })

  useEffectAsync(async () => {
    if (device?.attributes?.deviceImage && device?.uniqueId) {
      const imageUrl = `/api/media/${device.uniqueId}/${device.attributes.deviceImage}`
      setDeviceImageUrl(imageUrl)
    } else {
      setDeviceImageUrl(DEFAULT_IMAGE_URL)
    }
  }, [device])

  if (loading || !item) {
    return (
      <Root>
        <AppBar position="sticky" color="inherit">
          <Toolbar>
            <IconButton color="inherit" edge="start" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6">{t("sharedLoading")}</Typography>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 64px)",
          }}
        >
          <Typography variant="h6">{t("sharedLoading")}</Typography>
        </Box>
      </Root>
    )
  }

  return (
    <Root>
      <AppBar position="sticky" color="inherit">
        <Toolbar>
          <IconButton onClick={() => navigate(-1)} edge="start" color="inherit">
            <BackIcon />
          </IconButton>
          <Typography variant="h6">{t("sharedBack")}</Typography>
        </Toolbar>
      </AppBar>

      <Content>
        <Container
          maxWidth="lg"
          sx={{
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          }}
        >
          <Header sx={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: 280,
            padding: 0,
          }}>
            <Box sx={{
              display: 'flex',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${backgroundHeader})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}>
              {/* Seção Esquerda */}
              <Box sx={{
                flex: '0 0 33.3333%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: theme.spacing(3),
                borderRight: '1px solid rgba(255,255,255,0.3)',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CourseIconContainer sx={{ mr: 2 }}>
                    <NearMeIcon style={{
                      fontSize: 40,
                      transform: `rotate(${item.course || 0}deg)`
                    }} />
                  </CourseIconContainer>
                  <Box>
                    <CategoryTitle sx={{ fontSize: 14 }}>DIREÇÃO</CategoryTitle>
                    <CategoryValue sx={{ fontSize: 18 }}>
                      {courseFormatter(item.course || 0, t)}
                    </CategoryValue>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <CategoryTitle sx={{ fontSize: 12 }}>{t('positionSpeed')}</CategoryTitle>
                    <CategoryValue sx={{ fontSize: 18 }}>
                      {formatSpeed(item.speed, speedUnit, t)}
                    </CategoryValue>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <CategoryTitle sx={{ fontSize: 12 }}>{t('sharedDistance')}</CategoryTitle>
                    <CategoryValue sx={{ fontSize: 18 }}>
                      {formatDistance(item.attributes?.distance || 0, distanceUnit, t)}
                    </CategoryValue>
                  </Box>
                </Box>
              </Box>

              {/* Seção Central */}
              <Box sx={{
                flex: '0 0 33.3333%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: theme.spacing(3),
                borderRight: '1px solid rgba(255,255,255,0.3)',
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}>
                <Avatar
                  src={deviceImageUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    border: '3px solid #fff',
                    boxShadow: 3,
                    mb: 2
                  }}
                />
                <DeviceImageName sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  mb: 1,
                  lineHeight: 1.2
                }}>
                  {device?.name || item.deviceId}
                </DeviceImageName>
                <Address sx={{
                  fontSize: 13,
                  lineHeight: 1.3,
                  mb: 1
                }}>
                  {item.address}
                </Address>
                <ImagePlateName sx={{
                  fontSize: 14,
                  fontWeight: 600
                }}>
                  {device?.contact || item.attributes?.form?.plate || ''}
                </ImagePlateName>
              </Box>

              {/* Seção Direita */}
              <Box sx={{
                flex: '0 0 33.3333%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: theme.spacing(3),
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}>
                <Box sx={{ mb: 3 }}>
                  <CategoryTitle sx={{ fontSize: 12 }}>{t('positionStatus')}</CategoryTitle>
                  <CategoryValue sx={{ fontSize: 18 }}>
                    {getStatusText(device?.status, t)}
                  </CategoryValue>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <CategoryTitle sx={{ fontSize: 12 }}>{t('positionFixTime')}</CategoryTitle>
                  <CategoryValue sx={{ fontSize: 18 }}>
                    {formatDate(item.fixTime, dateTimeFormat)}
                  </CategoryValue>
                </Box>
                <Box>
                  <CategoryTitle sx={{ fontSize: 12 }}>{t('positionAlarm')}</CategoryTitle>
                  <CategoryValue sx={{
                    fontSize: 18,
                    color: item.attributes?.alarm ? theme.palette.error.light : '#fff'
                  }}>
                    {item.attributes?.alarm
                      ? formatAlarm(item.attributes.alarm, t)
                      : formatBoolean(false, t)}
                  </CategoryValue>
                </Box>
              </Box>
            </Box>
          </Header>

          {/* Info cards - Properly organized */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(2),
            }}
          >
            {[  // Lista dinâmica com destaque baseado em dados
              {
                title: "Neste dia",
                value: formatDistance(trip.distance, distanceUnit, t),
                icon: <PlayCircleFilledIcon />,
                bg: "#5C73DB",
              },
              {
                title: t("positionBattery"),
                value: item.attributes?.batteryLevel
                  ? `${Number.parseFloat(item.attributes.batteryLevel).toFixed(0) || 0}%`
                  : item.attributes?.battery
                    ? `${Number.parseFloat(item.attributes.battery).toFixed(0) || 0} V`
                    : t("deviceStatusUnknown"),
                icon: <BatteryCharging90Icon />,
                bg: item.attributes?.batteryLevel < 20 ? "#D32F2F" : "#388E3C",
              },
              {
                title: t("reportAverageSpeed"),
                value: formatSpeed(trip.averageSpeed || 0, speedUnit, t),
                icon: <SpeedIcon />,
                bg: "#7B1FA2",
              },
              {
                title: t("positionFuel"),
                value: formatVolume(trip.spentFuel, volumeUnit, t),
                icon: <LocalGasStationIcon />,
                bg: trip.spentFuel === 0 ? "#616161" : "#0277BD",
              },
            ].map((card, idx) => (
              <CardPosition
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  minHeight: 130,
                  padding: theme.spacing(2),
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${card.bg} 0%, ${card.bg}AA 100%)`,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  transition: "transform 0.2s ease-in-out",
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: "0 6px 30px rgba(0,0,0,0.15)",
                  }
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <CardTitle sx={{ color: '#E3F2FD', fontSize: 13 }}>{card.title}</CardTitle>
                  <CardValue sx={{ color: '#FFFFFF', fontSize: 20 }}>{card.value}</CardValue>
                </CardContent>
                <CardIcon
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    width: 48,
                    height: 48,
                    '& svg': {
                      fontSize: 26,
                    },
                  }}
                >
                  {card.icon}
                </CardIcon>
              </CardPosition>
            ))}
          </Box>

          {/* Details accordion */}
          <StyledAccordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="details-content" id="details-header">
              <Typography variant="subtitle1">{t("sharedShowDetails")}</Typography>
            </AccordionSummary>
            <StyledAccordionDetails>
              <Paper sx={{ overflow: "hidden", borderRadius: 1 }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("stateName")}</TableCell>
                      <TableCell>{t("sharedName")}</TableCell>
                      <TableCell>{t("stateValue")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.getOwnPropertyNames(item)
                      .filter((it) => it !== "attributes")
                      .map((property) => (
                        <TableRow key={property} hover>
                          <TableCell>{property}</TableCell>
                          <TableCell>
                            <strong>{t(prefixString("position", property))}</strong>
                          </TableCell>
                          <TableCell>
                            <PositionValue position={item} property={property} />
                          </TableCell>
                        </TableRow>
                      ))}
                    {Object.getOwnPropertyNames(item.attributes || {}).map((attribute) => (
                      <TableRow key={attribute} hover>
                        <TableCell>{attribute}</TableCell>
                        <TableCell>
                          <strong>
                            {t(prefixString("position", attribute)) || t(prefixString("device", attribute))}
                          </strong>
                        </TableCell>
                        <TableCell>
                          <PositionValue position={item} attribute={attribute} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </StyledAccordionDetails>
          </StyledAccordion>
        </Container>
      </Content>
    </Root>
  )
}

export default PositionPage